import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import {
  listActiveWorkflowRuns,
  normalizeWorkflowRun,
  type WorkflowRun,
} from '../api/workflowRuns'
import { DynamicIslandWorkflowDetailView } from '../components/theme/dynamic-island/detail/DynamicIslandWorkflowDetailView'
import { useReportDynamicIslandActivities, useDynamicIsland } from '../components/theme'
import { mapWorkflowRunToActivity } from '../lib/dynamicIsland/mapWorkflowRunActivity'
import { isWorkflowRunActive } from '../lib/workflow/workflowRunDisplay'
import { workflowRunPagePath } from '../lib/workflow/workflowRunRoute'
import { workflowRunHub } from '../lib/workflowRunEventStream'
import { useTheme } from '../theme/ThemeContext'

function sortRuns(runs: WorkflowRun[]): WorkflowRun[] {
  return [...runs].sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt))
}

function currentStepLabel(run: WorkflowRun): string {
  const running = run.steps.find((step) => step.status === 'running')
  if (running) return running.label
  const latest = [...run.steps].reverse().find((step) => step.status !== 'queued')
  return latest?.label ?? '执行中'
}

export function WorkflowActiveRunIslandProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { openActivityDetail, closeActivityDetail, detailActivityId } = useDynamicIsland()
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const [runs, setRuns] = useState<WorkflowRun[]>([])
  const [dismissedRunIds, setDismissedRunIds] = useState<Set<string>>(() => new Set())

  const dismissRun = useCallback(
    (publicId: string) => {
      const activityId = `workflow-run:${publicId}`
      setDismissedRunIds((prev) => {
        if (prev.has(publicId)) return prev
        const next = new Set(prev)
        next.add(publicId)
        return next
      })
      if (detailActivityId === activityId) closeActivityDetail()
    },
    [closeActivityDetail, detailActivityId],
  )

  const refreshRuns = useCallback(async () => {
    const items = await listActiveWorkflowRuns()
    setRuns(sortRuns(items.filter((run) => isWorkflowRunActive(run.status))))
  }, [])

  useEffect(() => {
    void refreshRuns().catch(() => setRuns([]))
  }, [refreshRuns])

  useEffect(() => {
    if (runs.length === 0) return

    const unsubscribers = runs.map((run) =>
      workflowRunHub.subscribe(run.publicId, (next) => {
        const normalized = normalizeWorkflowRun(next)
        setRuns((prev) => {
          const merged = prev.filter((item) => item.publicId !== normalized.publicId)
          if (!isWorkflowRunActive(normalized.status)) {
            return sortRuns(merged)
          }
          return sortRuns([normalized, ...merged])
        })
      }),
    )

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe()
    }
  }, [runs.map((run) => run.publicId).join('\0')])

  const visibleRuns = useMemo(
    () => runs.filter((run) => !dismissedRunIds.has(run.publicId)),
    [runs, dismissedRunIds],
  )

  const activities = useMemo(
    () =>
      visibleRuns.map((run) => {
        const activityId = `workflow-run:${run.publicId}`
        const stepLabel = currentStepLabel(run)
        return mapWorkflowRunToActivity(run, {
          onOpenDetail: () => openActivityDetail(activityId),
          onClosePanel: () => dismissRun(run.publicId),
          detailTitle: `工作流 · ${stepLabel}`,
          renderDetail: () => (
            <DynamicIslandWorkflowDetailView
              run={run}
              angular={angular}
              onOpenRunPage={() => navigate(workflowRunPagePath(run.publicId))}
            />
          ),
        })
      }),
    [visibleRuns, navigate, angular, openActivityDetail, dismissRun],
  )

  useReportDynamicIslandActivities(activities)

  return <>{children}</>
}
